'use client';
import { useDebounceFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useRef } from 'react';
import { AutoSizer, Grid } from 'react-virtualized';
import { BaseFillCheckPick3 } from '@/app/components/icons/BaseFillCheckPick3';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { Loading } from '@/app/components/icons/Loading';
import { ProductView } from '@/app/components/icons/ProductView';
import { ElementCard } from '@/app/components/shared/ElementCard';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import { SEARCH_EMPTY_ICON } from '@/app/constants';
import { useCheckEnableCreateElement, useExpose, useExtraTag, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import type { ElementListProps } from '../../types';

const GRID_PARAMS = {
  columnCount: 5,
  rowHeight: 147,
};

export const ElementList = ({
  className,
  selectedElements,
  tagSelectors,
  disabledIds,
  tags,
  elements,
  isLoading = false,
  hasNextPage = false,
  enableCreate = true,
  onLoadMore,
  toggleElementSelection,
  deeps,
  onExpose,
}: ElementListProps) => {
  const t = useTranslations();
  const { allTag } = useExtraTag();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const pathname = usePathname();
  const { openReferenceModalWithData, userInfo } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
    userInfo: state.userInfo,
  }));

  const { checkCreateElementEnable } = useCheckEnableCreateElement({
    onSuccess: () => {
      const tagId = tagSelectors?.filter((it) => it !== allTag.id) || [];
      openReferenceModalWithData?.({
        referenceModalOpen: true,
        element: {
          modality: 'image',
          labels: tagId.length > 1 ? [] : tags?.filter((it) => it.id === tagId[0]),
        },
        referenceModalMode: 'create',
        referenceModalType: 'reference',
        referenceModalFrom: 'select',
      });
      reportEvent('create_element_page_view', {
        refer_element: 'element_selector',
      });
    },
  });

  const loadMore = useDebounceFn(
    () => {
      onLoadMore?.();
    },
    { wait: 200 },
  );

  const exposureRefs = useRef<HTMLDivElement[]>([]);

  const { initObservedIndices } = useExpose(
    (idx) => {
      onExpose?.(idx);
    },
    exposureRefs.current,
    elements,
    [0.1, 1],
  );

  useEffect(() => {
    initObservedIndices();
  }, [deeps]);

  return (
    <div className={cn(className)}>
      {elements.length === 0 && isLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <Loading className="mx-auto mt-4 animate-spin" />
        </div>
      ) : (
        <AutoSizer>
          {({ width, height }) => {
            const { columnCount, rowHeight } = GRID_PARAMS;
            const columnWidth = Math.floor(width / columnCount);
            const rowCount = Math.ceil(((elements?.length ?? 0) + (enableCreate ? 1 : 0)) / columnCount);
            const gridWidth = width;
            const gridHeight = height;

            return (
              <Grid
                className="hide-scrollbar overflow-x-hidden"
                width={gridWidth}
                height={gridHeight}
                rowCount={rowCount}
                columnCount={columnCount}
                rowHeight={rowHeight} // 行高增加间距
                columnWidth={columnWidth} // 列宽增加间距
                noContentRenderer={() => (
                  <SearchEmpty
                    emptySrc={SEARCH_EMPTY_ICON.selector}
                    textClassName="text-xs"
                    actionText={!userInfo ? t('header.navLogin') : undefined}
                    onAction={
                      !userInfo
                        ? () => {
                            openLoginDialog();
                            reportEvent('login_pop_exposure', {
                              refer_element: 'elements-selector-empty',
                              refer_page: pathname,
                            });
                          }
                        : undefined
                    }
                  />
                )}
                cellRenderer={({ columnIndex, rowIndex, style }) => {
                  const index = rowIndex * columnCount + columnIndex;
                  // 调整style以添加间距
                  const adjustedStyle = {
                    ...style,
                    left: Number(style.left),
                    top: Number(style.top),
                    width: columnWidth,
                    height: rowHeight,
                  };

                  if (index === 0 && enableCreate) {
                    return (
                      <div
                        style={adjustedStyle}
                        key="create-new"
                        onClick={() => {
                          checkCreateElementEnable({
                            query: {},
                          });
                        }}
                      >
                        <div className="flex h-[137px] w-[106px] cursor-pointer items-center justify-center rounded-8 bg-system-black24">
                          <div className="flex flex-col items-center justify-center">
                            <BaseLinePlus className="text-lg" />
                          </div>
                        </div>
                      </div>
                    );
                  }
                  const element = elements[index - (enableCreate ? 1 : 0)];
                  if (!element) return null;
                  const disableSelect = disabledIds?.includes(element.id || '');
                  const checked = selectedElements.some((item) => item.id === element.id) || disableSelect;
                  const isLast = index === elements.length - (enableCreate ? 0 : 1);

                  return (
                    <Fragment key={element.id}>
                      <div
                        style={adjustedStyle}
                        ref={(el: HTMLDivElement) => {
                          exposureRefs.current[index] = el;
                        }}
                        data-index={index}
                      >
                        <ElementCard
                          type="element"
                          element={element}
                          className={cn(checked && 'border-system-blue02')}
                          tag={element.tag}
                          onClick={() => {
                            if (!disableSelect) {
                              toggleElementSelection?.(element);
                            }
                          }}
                          hoverOperations={[
                            element?.type === 'user' &&
                              !['approved', 'pending'].includes(element?.share_meta?.status || '') && (
                                <BaseLineEdit
                                  key="edit"
                                  onClick={() => {
                                    openReferenceModalWithData?.({
                                      referenceModalOpen: true,
                                      element,
                                      referenceModalMode: 'edit',
                                      referenceModalFrom: 'select',
                                    });
                                    reportEvent('element_operation', {
                                      operation_type: 'edit',
                                    });
                                  }}
                                />
                              ),
                            <ProductView
                              key="view"
                              onClick={() => {
                                openReferenceModalWithData?.({
                                  referenceModalOpen: true,
                                  element,
                                  referenceModalMode: 'view',
                                  referenceModalFrom: 'select',
                                  referenceModalFromPage: 'reference_tool',
                                });
                                reportEvent('element_operation', {
                                  operation_type: 'view_detail',
                                });
                              }}
                            />,
                          ]}
                          checkSlot={
                            <div className="absolute top-[5px] left-[5px] z-10">
                              <CheckboxFill
                                disabled={disableSelect}
                                checked={checked}
                                customIcon={disableSelect && checked ? <BaseFillCheckPick3 /> : undefined}
                                onCheckedChange={() => {
                                  toggleElementSelection?.(element);
                                }}
                                className={cn(
                                  'block text-2xl opacity-0 transition-opacity disabled:cursor-not-allowed group-hover:opacity-100',
                                  checked && 'opacity-100',
                                )}
                              />
                            </div>
                          }
                        />
                      </div>

                      {isLast && hasNextPage && isLoading && <Loading className="mx-auto mt-4 animate-spin" />}
                    </Fragment>
                  );
                }}
                onScroll={({ scrollTop, scrollHeight, clientHeight }) => {
                  if (scrollHeight - scrollTop - clientHeight < 100 && !isLoading && hasNextPage) {
                    loadMore.run();
                  }
                }}
              />
            );
          }}
        </AutoSizer>
      )}
    </div>
  );
};
